import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Telescope } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { SignalBacktestPanel } from "../components/SignalBacktestPanel";
import { useAuth } from "../App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const usd = (v) =>
  v == null ? "—" : v >= 1e9 ? `$${(v / 1e9).toFixed(1)}B` : `$${(v / 1e6).toFixed(1)}M`;
const pct = (v, dp = 1) => (v == null ? "—" : `${(v * 100).toFixed(dp)}%`);

const CONV_STYLE = {
  SWARM: "bg-amber-500/15 text-amber-300 border-amber-500/50",
  SOLO: "text-emerald-300 border-emerald-500/40",
  PAIR: "text-muted-foreground border-border",
};
const CONV_LABEL = {
  SWARM: "SWARM · 3+ SCOUTS",
  SOLO: "SOLO",
  PAIR: "PAIR · DEAD ZONE",
};

const SignalRow = ({ r }) => (
  <tr className="border-b border-border/40 hover:bg-muted/30"
      data-testid={`scouts-row-${r.symbol}-${r.accepted_date}`}>
    <td className="py-2 px-2 font-bold">{r.symbol}</td>
    <td className="py-2 px-2">
      <Badge variant="outline" className={`text-[10px] ${CONV_STYLE[r.conviction]}`}>
        {r.conviction === "SWARM" ? `SWARM ×${r.n_cohort_conv}` : CONV_LABEL[r.conviction]}
      </Badge>
    </td>
    <td className="py-2 px-2 text-xs">{r.fund}</td>
    <td className="py-2 px-2 font-mono text-xs text-muted-foreground">{r.accepted_date}</td>
    <td className="py-2 px-2 text-right font-mono text-xs">{usd(r.mcap)}</td>
    <td className="py-2 px-2 text-right font-mono text-xs">{r.holders}</td>
    <td className="py-2 px-2 text-right font-mono text-xs">{usd(r.delta_value)}</td>
    <td className={`py-2 px-2 text-right font-mono ${
      (r.excess_since || 0) >= 0 ? "text-emerald-300" : "text-rose-300"}`}>
      {pct(r.excess_since)}
    </td>
    <td className="py-2 px-2 text-xs text-muted-foreground">{r.sector || "—"}</td>
  </tr>
);

const SignalTable = ({ rows, testid }) => (
  <table className="w-full text-sm" data-testid={testid}>
    <thead className="border-b border-border text-muted-foreground text-xs">
      <tr>
        <th className="py-2 px-2 text-left">Symbol</th>
        <th className="py-2 px-2 text-left">Conviction</th>
        <th className="py-2 px-2 text-left">Fund</th>
        <th className="py-2 px-2 text-left">Disclosed</th>
        <th className="py-2 px-2 text-right">Mkt cap</th>
        <th className="py-2 px-2 text-right">Holders</th>
        <th className="py-2 px-2 text-right">Δ value</th>
        <th className="py-2 px-2 text-right">Excess since</th>
        <th className="py-2 px-2 text-left">Sector</th>
      </tr>
    </thead>
    <tbody>{rows.map((r, i) => <SignalRow key={`${r.symbol}-${i}`} r={r} />)}</tbody>
  </table>
);

export default function DiscoveryScoutsPage() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [hidePairs, setHidePairs] = useState(false);

  const load = useCallback(async () => {
    const res = await fetch(`${API}/signals/discovery-scouts`, {
      headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) setData(await res.json());
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/signals/discovery-scouts/refresh`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` } });
      const body = await res.json();
      if (!res.ok) throw new Error(body.detail || "refresh failed");
      toast.success(`Scouts refreshed — ${body.n_active} active signals`);
      await load();
    } catch (e) {
      toast.error(String(e.message || e));
    } finally { setBusy(false); }
  };

  const rows = (data?.rows || []).filter((r) => !hidePairs || r.conviction !== "PAIR");
  const active = rows.filter((r) => r.active);
  const older = rows.filter((r) => !r.active);

  return (
    <div className="p-6 space-y-6" data-testid="discovery-scouts-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Telescope className="w-6 h-6 text-emerald-300" /> Discovery Scouts
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
            Under-the-radar small-cap signal — validated scout funds opening new
            positions in &lt;$2B names held by ≤5 tracked institutions. OOS
            top-quintile: +7.9% fwd-63d excess (t=3.2). Conviction is bimodal:
            SOLO and SWARM (3+ scouts) both work — a PAIR is the dead zone.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant={hidePairs ? "default" : "outline"} size="sm"
                  onClick={() => setHidePairs(!hidePairs)} data-testid="scouts-pair-filter">
            Hide pairs
          </Button>
          <Button variant="outline" size="sm" onClick={refresh} disabled={busy}
                  data-testid="scouts-refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-1 ${busy ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      {!data ? (
        <Card><CardContent className="py-10 text-center text-muted-foreground">
          Not computed yet — hit Refresh to build the scout feed.
        </CardContent></Card>
      ) : (
        <>
          <div className="text-xs text-muted-foreground" data-testid="scouts-meta">
            {data.n_signals} signals · {data.n_active} in active window (≤90d) ·
            computed {data.computed_at?.slice(0, 16).replace("T", " ")} UTC
          </div>
          <SignalBacktestPanel slug="discovery-scouts" lineColor="#34d399"
                               accent="text-emerald-300" />
          <Card data-testid="scouts-active-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">
                Active signals <span className="text-muted-foreground font-normal">
                (disclosed in last 90 days — inside the measured edge window)</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              {active.length ? <SignalTable rows={active} testid="scouts-active-table" />
                : <p className="text-sm text-muted-foreground py-4">No active signals.</p>}
            </CardContent>
          </Card>
          <Card data-testid="scouts-history-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">
                Recent history <span className="text-muted-foreground font-normal">
                (window expired — outcome tracking)</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              {older.length ? <SignalTable rows={older} testid="scouts-history-table" />
                : <p className="text-sm text-muted-foreground py-4">—</p>}
            </CardContent>
          </Card>
          <Card data-testid="scouts-cohort-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">
                Scout cohort <span className="text-muted-foreground font-normal">
                (graded pre-Jul-2024, skill persists OOS — orthogonal to knife cohort)</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border text-muted-foreground text-xs">
                  <tr>
                    <th className="py-2 px-2 text-left">Fund</th>
                    <th className="py-2 px-2 text-right"># discoveries (train)</th>
                    <th className="py-2 px-2 text-right">EB grade</th>
                    <th className="py-2 px-2 text-right">Avg fwd-63d excess</th>
                    <th className="py-2 px-2 text-right">Hit rate</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.cohort || []).map((f) => (
                    <tr key={f.cik} className="border-b border-border/40"
                        data-testid={`scouts-cohort-${f.cik}`}>
                      <td className="py-1.5 px-2">{f.name}</td>
                      <td className="py-1.5 px-2 text-right font-mono">{f.n_events}</td>
                      <td className="py-1.5 px-2 text-right font-mono text-sky-300">
                        {f.eb_grade != null ? pct(f.eb_grade) : "—"}
                      </td>
                      <td className="py-1.5 px-2 text-right font-mono text-emerald-300">
                        {pct(f.avg_fwd63_excess)}
                      </td>
                      <td className="py-1.5 px-2 text-right font-mono">{pct(f.hit_rate, 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
